/* ============================================================================
   Mega Menu — JavaScript controller
   ============================================================================
   Handles:
     - 150ms hover-out delay (prevents flicker when cursor crosses gaps)
     - Keyboard navigation (Escape to close, Tab to move through)
     - Mobile accordion toggle
     - Sets aria-expanded correctly on trigger elements

   Wraps as a custom element so it's self-contained and doesn't pollute globals.
   ============================================================================ */

class MegaMenuTrigger extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('[data-mega-menu]');
    this.trigger = this.querySelector('[data-mega-menu-trigger]');
    this.closeTimeout = null;
    this.hoverDelay = 150; // ms

    if (!this.panel || !this.trigger) return;

    this.setupDesktop();
    this.setupKeyboard();
    this.setupMobile();
  }

  setupDesktop() {
    // Desktop: open on hover, close on mouse leave (with delay)
    const mq = window.matchMedia('(min-width: 750px)');

    const handleMouseEnter = () => {
      if (!mq.matches) return;
      clearTimeout(this.closeTimeout);
      this.open();
    };

    const handleMouseLeave = () => {
      if (!mq.matches) return;
      this.closeTimeout = setTimeout(() => this.close(), this.hoverDelay);
    };

    this.addEventListener('mouseenter', handleMouseEnter);
    this.addEventListener('mouseleave', handleMouseLeave);
  }

  setupKeyboard() {
    // Open on focus (keyboard users)
    this.trigger.addEventListener('focus', () => {
      if (window.matchMedia('(min-width: 750px)').matches) {
        this.open();
      }
    });

    // Close on Escape
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
        this.trigger.focus();
      }
    });

    // Close when focus leaves the menu entirely
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) {
        this.close();
      }
    });
  }

  setupMobile() {
    // Mobile: click trigger to toggle
    const mq = window.matchMedia('(max-width: 749px)');

    this.trigger.addEventListener('click', (e) => {
      if (!mq.matches) return;
      e.preventDefault();

      const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    });
  }

  open() {
    this.panel.setAttribute('data-open', 'true');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.panel.setAttribute('data-open', 'false');
    this.trigger.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('mega-menu-trigger', MegaMenuTrigger);
